import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { validateAcknowledgementField } from "./scriptcoacknowledgements.helper";

const ScriptCoAcknowledgements = ({
  data,
  onDataChange,
  onValidationChange,
  showErrors,
  consentHeader,
  consentDetail,
  disabled = false,
}) => {
  const [formData, setFormData] = useState({
    attested: data?.attested || false,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    const error = validateAcknowledgementField("attested", formData.attested);
    setErrors(error ? { attested: error } : {});
  }, [formData]);

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(disabled ? true : Object.keys(errors).length === 0);
    }
  }, [errors, disabled, onValidationChange]);

  const handleChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: checked };
      if (onDataChange) onDataChange(next);
      return next;
    });
  };

  const getErrorProps = (name) => {
    const isInvalid = !!errors[name];
    return {
      error: isInvalid && showErrors && !disabled,
      helperText: showErrors && !disabled ? errors[name] : "",
    };
  };

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "12px",
        p: 2.5,
        border: "1px solid #E8E8E9",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        width: "100%",
        boxSizing: "border-box",
        flex: 1,
      }}
    >
      <Typography
        sx={{ fontWeight: 600, fontSize: "16px", color: "#403C6F" }}
      >
        Acknowledgements
      </Typography>

      {consentDetail && (
        <Box
          sx={{
            backgroundColor: "#F9F9F9",
            borderRadius: "8px",
            p: 2,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Typography
            sx={{ fontSize: "13px", color: "#000000", fontWeight: 400 }}
          >
            {consentDetail}
          </Typography>
        </Box>
      )}

      <Box sx={{ display: "flex", flexDirection: "column" }}>
        <FormControlLabel
          control={
            <Checkbox
              name="attested"
              size="small"
              disabled={disabled}
              checked={formData.attested || disabled}
              onChange={handleChange}
              sx={{
                "&.Mui-checked": { color: "#403C6F !important" },
                color: getErrorProps("attested").error
                  ? "#d32f2f !important"
                  : "#D8D6E2",
                p: 0.5,
                mr: 1,
                ml: 1,
              }}
            />
          }
          label={
            <Typography
              sx={{
                fontSize: "13px",
                color: getErrorProps("attested").error ? "#d32f2f" : "#4B4B4B",
                fontWeight: 500,
                lineHeight: 1.4,
              }}
            >
              {consentHeader ||
                "I have confirmed the member has agreed to have the above medication shipped under the applicable laws of the program offered by RxManage."}
              <span style={{ color: "#E53935", marginLeft: "4px" }}>*</span>
            </Typography>
          }
          sx={{ m: 0, alignItems: "center" }}
        />
        {getErrorProps("attested").error && (
          <Typography
            sx={{ color: "#d32f2f", fontSize: "12px", fontWeight: 500, mt: 0.5, ml: 4 }}
          >
            {getErrorProps("attested").helperText || "Attestation is required."}
          </Typography>
        )}
      </Box>
    </Box>
  );
};

ScriptCoAcknowledgements.propTypes = {
  data: PropTypes.object.isRequired,
  onDataChange: PropTypes.func.isRequired,
  onValidationChange: PropTypes.func,
  showErrors: PropTypes.bool,
  consentHeader: PropTypes.string,
  consentDetail: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ScriptCoAcknowledgements;
